import { z } from "zod/v4";

import { DiscountTierDtoSchema } from "./discount-tiers";
import { ShippingDiscountDtoSchema } from "./shipping-discount";
import { UserInterfaceDtoSchema } from "./user-interfaces";

export const DiscountUnitTypeList = ["unit", "dollar"] as const;
export type DiscountUnitType = (typeof DiscountUnitTypeList)[number];

export const DiscountDiscountTypeList = ["percentage", "dollar"] as const;
export type DiscountDiscountType = (typeof DiscountDiscountTypeList)[number];

export const DiscountSettingFieldList = ["unitType", "discountType"] as const;
export type DiscountSettingFieldType =
  (typeof DiscountSettingFieldList)[number];

export const DiscountApplicationMethodList = ["perItem", "perOrder"] as const;
export type DiscountApplicationMethodType =
  (typeof DiscountApplicationMethodList)[number];

// TODO: BB this is where the amount off X will go
export const DiscountApplicationTypeList = [
  "tieredDiscount",
  "cartDiscount",
  "freeGift",
  "freeShipping",
  "",
] as const;

export type DiscountApplicationType =
  (typeof DiscountApplicationTypeList)[number];

export type DiscountApplicationTypeNotEmpty = Exclude<
  DiscountApplicationType,
  ""
>;

export const OfferLikeObjectSchema = z.object({
  id: z.uuid(),
  enabled: z.boolean().default(false),
  isTest: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  name: z.string(),
  // offerProducts: OfferProductDtoSchema.array().optional().default([]),
  unitType: z.enum(DiscountUnitTypeList).default("dollar"),
  discountType: z.enum(DiscountDiscountTypeList).default("dollar"),
  tiers: DiscountTierDtoSchema.array().default([]),
  maximumDollarDiscount: z.number().nullish(),
  discountApplicationType: z.enum(DiscountApplicationTypeList),
  discountApplicationMethod: z.enum(DiscountApplicationMethodList),
  combinesWithIntelligems: z.boolean(),
  combinesWithShopify: z.boolean(),
  shouldRejectCodes: z.boolean(),
  shippingDiscount: ShippingDiscountDtoSchema.nullish(),
});

export type OfferLikeObject = z.infer<typeof OfferLikeObjectSchema>;

export const OfferDtoSchema = z.object({
  id: z.uuid().describe("Unique identifier for the offer"),
  variationId: z.uuid().describe("Variation this offer belongs to"),
  enabled: z
    .boolean()
    .default(false)
    .describe("Whether the offer is active and applying to customers"),
  isTest: z
    .boolean()
    .default(false)
    .describe("Whether the offer is in test mode"),
  isArchived: z
    .boolean()
    .default(false)
    .describe("Whether the offer has been archived"),
  name: z.string().describe("Internal name for the offer"),
  // offerProducts: OfferProductDtoSchema.array().optional().default([]),
  unitType: z
    .enum(DiscountUnitTypeList)
    .default("dollar")
    .describe("Discount basis: 'unit' (per item) or 'dollar' (fixed amount)"),
  discountType: z
    .enum(DiscountDiscountTypeList)
    .default("dollar")
    .describe("How discount is calculated: 'percentage' or 'dollar'"),
  tiers: DiscountTierDtoSchema.array()
    .default([])
    .describe(
      "Volume discount tiers (e.g., buy 2 get 10% off, buy 3 get 15% off)"
    ),
  maximumDollarDiscount: z.coerce
    .number()
    .nullish()
    .describe(
      "Maximum dollar amount that can be discounted (cap on percentage discounts)"
    ),
  discountApplicationType: z
    .enum(DiscountApplicationTypeList)
    .describe(
      "Type of discount: 'tieredDiscount', 'cartDiscount', 'freeGift', 'freeShipping'"
    ),
  discountApplicationMethod: z
    .enum(DiscountApplicationMethodList)
    .describe("How to apply discount: 'perItem' or 'perOrder'"),
  combinesWithIntelligems: z
    .boolean()
    .describe("Whether this offer stacks with other Intelligems discounts"),
  combinesWithShopify: z
    .boolean()
    .describe("Whether this offer stacks with native Shopify discounts"),
  shouldRejectCodes: z
    .boolean()
    .describe("Whether to reject discount codes when this offer is applied"),
  shippingDiscount: ShippingDiscountDtoSchema.nullable().describe(
    "Shipping discount configuration if applicable"
  ),
});

export type OfferDto = z.infer<typeof OfferDtoSchema>;

export const OfferWithIdSchema = OfferDtoSchema.extend({
  userInterfaces: UserInterfaceDtoSchema.array().optional(),
});

export type OfferWithIdType = z.infer<typeof OfferWithIdSchema>;

export const CreateOfferDtoSchema = OfferDtoSchema.extend({
  id: z.string().optional(),
}).omit({ tiers: true, shippingDiscount: true });

export type CreateOfferDto = z.infer<typeof CreateOfferDtoSchema>;

export const PatchOfferDtoSchema = z.object({
  id: z.uuid().optional(),
  enabled: z.boolean().default(false),
  isTest: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  name: z.string(),
  unitType: z.enum(DiscountUnitTypeList).default("dollar"),
  discountType: z.enum(DiscountDiscountTypeList).default("dollar"),
  maximumDollarDiscount: z.number().nullish(),
  discountApplicationType: z.enum(DiscountApplicationTypeList),
  discountApplicationMethod: z.enum(DiscountApplicationMethodList),
  combinesWithIntelligems: z.boolean(),
  combinesWithShopify: z.boolean(),
  shouldRejectCodes: z.boolean(),
});

export type PatchOfferDto = z.infer<typeof PatchOfferDtoSchema>;
